import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ProtectedImage from "./ProtectedImage";
import { AlertCircle, Check, ChevronDown, ChevronLeft, ChevronRight, X } from "react-feather";
import { useSession } from "../store/session";
import { useHelpersSheet } from "../store/helpersSheet";
import { HelperRequestError, getHelperDetail, getHelpers } from "../store/api";
import {
  actionParts, briefPreview, groupHelpers, groupTask, helperTime, isActive, mergeSteps, pollAnchor,
  quietHint, STATE_WORD, startedLine, stateTone, safeHelperImage, type HelperGroup } from "../lib/helperPresentation";
import { usePollWhileVisible } from "../lib/usePollWhileVisible";
import type { Helper, HelperDetail, HelperList, HelperState, HelperStep } from "../lib/types";
import "../styles/helpers.css";
import { HelperBot } from "./HelperFaces";
import SwapText from "./SwapText";

/**
 * The Helpers sheet: what each helper (sub-agent) of this chat is doing right
 * now, and a drill-in for one helper. Phone: bottom sheet. Laptop / mouse:
 * centred dialog. Nothing is fetched unless it is open; it polls every 4 s only
 * while open and the page is visible. Every server string renders as text.
 */
const POLL_MS = 4000;
/** The detail leads with the newest images and the helper's own words; every step folds away. */
const SHOTS_SHOWN = 4, SAID_SHOWN = 4;

type Target = { id: string; run?: string };
const targetKey = (t: Target) => `${t.run ?? ""}/${t.id}`;

export default function HelpersSheet() {
  const companyId = useHelpersSheet((s) => s.companyId);
  const activeCompanyId = useSession((s) => s.activeCompanyId);
  // Switching chats closes the sheet (it belongs to the chat it was opened in).
  useEffect(() => {
    if (companyId && companyId !== activeCompanyId) useHelpersSheet.getState().close(false);
  }, [companyId, activeCompanyId]);
  if (!companyId || companyId !== activeCompanyId) return null;
  return createPortal(<HelpersDialog key={companyId} companyId={companyId} />, document.body);
}

function HelpersDialog({ companyId }: { companyId: string }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const listBody = useRef<HTMLDivElement>(null);
  const [list, setList] = useState<HelperList | null>(null);
  const [error, setError] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [selected, setSelected] = useState<Target | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const close = useCallback(() => useHelpersSheet.getState().close(true), []);

  useEffect(() => {
    const panel = dialog.current;
    if (!panel) return;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (!panel.open) panel.showModal(); // native focus containment + inert page behind
    // Start focus on the sheet itself, not the ✕: showModal would otherwise focus the ✕ and
    // draw its focus ring before anyone touched it (28 Sep, Julian). Tab still reaches it.
    panel.querySelector<HTMLElement>(".helpersSheet__panel")?.focus({ preventScroll: true });
    return () => { if (panel.open) panel.close(); document.body.style.overflow = overflow; };
  }, []);

  // One ticker for every age on screen.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const seq = useRef(0);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const body = await getHelpers(companyId);
      if (mine !== seq.current) return;
      const at = Date.now();
      setList(body); setError(false); setFetchedAt(at); setNow(at);
    } catch {
      if (mine === seq.current) setError(true);
    }
  }, [companyId]);
  // The list pauses while one helper is open (that view polls itself).
  usePollWhileVisible(load, POLL_MS, !selected);

  const groups = useMemo(() => (list ? groupHelpers(list.helpers, list.runs) : []), [list]);
  const summaryFor = (t: Target | null) =>
    t ? list?.helpers.find((h) => h.id === t.id && (t.run ? h.run === t.run : h.kind === "agent")) : undefined;

  const openDetail = (h: Helper) => setSelected({ id: h.id, ...(h.run ? { run: h.run } : {}) });
  // A tap/click on Back must not leave a focus ring on the row (28 Sep, Julian); keyboard
  // users (Escape, Enter/Space on Back) still get focus returned to the row they opened.
  const backToList = (fromKeyboard = true) => {
    const key = selected ? targetKey(selected) : "";
    setSelected(null);
    requestAnimationFrame(() => {
      if (!fromKeyboard) { dialog.current?.querySelector<HTMLElement>(".helpersSheet__panel")?.focus({ preventScroll: true }); return; }
      listBody.current?.querySelector<HTMLElement>(`[data-helper-key="${CSS.escape(key)}"]`)?.focus({ preventScroll: false });
    });
  };

  return (
    <dialog
      ref={dialog}
      className="helpersSheet"
      aria-labelledby={selected ? "helperDetail-title" : "helpersSheet-title"}
      onCancel={(event) => {
        event.preventDefault();
        if (dialog.current?.querySelector(".lightbox")) return; // Escape belongs to the open image preview
        if (selected) backToList(); else close();
      }}
      onKeyDown={(event) => event.stopPropagation()}
      onClick={(event) => { if (event.target === event.currentTarget) close(); }}
    >
      <div className="helpersSheet__panel" tabIndex={-1}>
        <div className="helpersSheet__grabber" aria-hidden="true" />
        <div className="helpersSheet__view" hidden={!!selected}>
          <header className="helpersSheet__head">
            <div className="helpersSheet__titles">
              <h2 id="helpersSheet-title">Helpers</h2>
              <p>What each helper in this chat is doing right now.</p>
            </div>
            <button type="button" className="helpersSheet__icon" aria-label="Close helpers" onClick={close}>
              <X width={20} height={20} aria-hidden="true" />
            </button>
          </header>
          <div ref={listBody} className="helpersSheet__body">
            {error && (
              <div className="helpersSheet__banner" role="alert">
                <span>Can’t load helpers — try again.</span>
                <button type="button" onClick={() => void load()}>Retry</button>
              </div>
            )}
            {!list && !error && <SkeletonRows />}
            {list && !list.helpers.length && <p className="helpersSheet__empty">No helpers in the last 2 hours.</p>}
            <div className={error ? "helpersSheet__groups is-stale" : "helpersSheet__groups"}>
              {groups.map((group) => {
                // Every group starts open (finished helpers show their faces and results too); any can be folded.
                const isCollapsed = group.collapsible && (collapsed[group.key] ?? false);
                return (
                  <HelperGroupSection
                    key={group.key}
                    group={group}
                    collapsed={isCollapsed}
                    onToggle={() => setCollapsed((c) => ({ ...c, [group.key]: !isCollapsed }))}
                    now={now}
                    sinceFetch={fetchedAt ? now - fetchedAt : 0}
                    onOpen={openDetail}
                  />
                );
              })}
            </div>
          </div>
        </div>
        {selected && (
          <HelperDetailView
            key={targetKey(selected)}
            companyId={companyId}
            target={selected}
            summary={summaryFor(selected)}
            now={now}
            onBack={backToList}
          />
        )}
      </div>
    </dialog>
  );
}

function SkeletonRows() {
  return (
    <div className="helpersSheet__skeleton" aria-label="Loading helpers" role="status">
      {[0, 1, 2].map((i) => <div key={i} className="helpersSheet__skelRow" />)}
    </div>
  );
}

function StateChip({ state }: { state: HelperState }) {
  return (
    <span className={`helperState is-${stateTone(state)} is-${state}`}>
      {state === "running" && <span className="helperState__live" aria-hidden="true" />}
      {state === "done" && <Check width={12} height={12} aria-hidden="true" />}
      {STATE_WORD[state]}
    </span>
  );
}

function HelperGroupSection({ group, collapsed, onToggle, now, sinceFetch, onOpen }: {
  group: HelperGroup; collapsed: boolean; onToggle(): void; now: number; sinceFetch: number; onOpen(h: Helper): void;
}) {
  const bodyId = `helpersGroup-${group.key.replace(/[^A-Za-z0-9_-]/g, "_")}`;
  const shared = groupTask(group.helpers);
  return (
    <section className={`helpersGroup is-${group.kind}`}>
      {group.collapsible ? (
        <button type="button" className="helpersGroup__label is-toggle" aria-expanded={!collapsed} aria-controls={bodyId} onClick={onToggle}>
          <span className="helpersGroup__title">{group.title}</span>
          <span className="helpersGroup__summary">· {group.summary}</span>
          <ChevronDown width={14} height={14} aria-hidden="true" className={collapsed ? "helpersGroup__chevron is-collapsed" : "helpersGroup__chevron"} />
        </button>
      ) : (
        <h3 className="helpersGroup__label">
          <span className="helpersGroup__title">{group.title}</span>
          <span className="helpersGroup__summary">· {group.summary}</span>
        </h3>
      )}
      {!collapsed && shared && <p className="helpersGroup__task">{shared}</p>}
      {!collapsed && (
        <ul id={bodyId} className="helpersGroup__rows">
          {group.helpers.map((h) => (
            <li key={targetKey({ id: h.id, run: h.run })}>
              <HelperRow helper={h} task={h.task !== shared ? h.task : undefined} now={now} sinceFetch={sinceFetch} onOpen={onOpen} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function HelperRow({ helper: h, task, now, sinceFetch, onOpen }: {
  helper: Helper; task?: string; now: number; sinceFetch: number; onOpen(h: Helper): void;
}) {
  const line = actionParts(h, now);
  const time = helperTime(h, now);
  const quiet = quietHint(h, sinceFetch);
  return (
    <button type="button" className={`helperRow is-${stateTone(h.state)}`} data-helper-key={targetKey({ id: h.id, run: h.run })} onClick={() => onOpen(h)}>
      <HelperBot bot={h.bot} id={h.id} state={h.state} size={40} />
      <span className="helperRow__main">
        <span className="helperRow__line1">
          <span className="helperRow__name">{h.name}</span>
          {time && <span className="helperRow__time">{time}</span>}
        </span>
        {task && <span className="helperRow__task">{task}</span>}
        <span className="helperRow__line2">
          <StateChip state={h.state} />
          {(line.text || line.age) && (
            <span className="helperRow__fact">
              {line.text && <SwapText text={line.text} shimmer={h.state === "running"} />}
              {line.age && <span className="helperRow__age">{line.text ? ` · ${line.age}` : line.age}</span>}
            </span>
          )}
          {quiet && <span className="helperRow__quiet">{quiet}</span>}
        </span>
      </span>
      <ChevronRight width={16} height={16} aria-hidden="true" className="helperRow__go" />
    </button>
  );
}

/* ------------------------------------------------------------------ detail */

function HelperDetailView({ companyId, target, summary, now, onBack }: {
  companyId: string; target: Target; summary?: Helper; now: number; onBack(fromKeyboard?: boolean): void;
}) {
  const [detail, setDetail] = useState<HelperDetail | null>(null);
  const [steps, setSteps] = useState<HelperStep[]>([]);
  const [error, setError] = useState<"load" | "gone" | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const [saidOpen, setSaidOpen] = useState(false);
  const [stepsOpen, setStepsOpen] = useState(false);
  const loaded = useRef<HelperStep[]>([]);
  const title = useRef<HTMLHeadingElement>(null);
  const seq = useRef(0);

  // Focus the title, not Back, so no focus ring shows on a button nobody pressed.
  useEffect(() => { title.current?.focus({ preventScroll: true }); }, []);

  const apply = useCallback((body: HelperDetail, offset: number) => {
    const merged = mergeSteps(loaded.current, offset, body.steps);
    loaded.current = merged;
    setSteps(merged);
    // With step ids the server's own "more after this page" is the truth (its window can
    // hold fewer steps than are loaded here); by position, the count still missing.
    const more = body.steps.every((s) => s.id) ? body.nextAfter !== null : merged.length < body.total;
    setDetail({ ...body, nextAfter: more ? merged.length : null });
    setError(null);
  }, []);

  const refresh = useCallback(async () => {
    const mine = ++seq.current;
    const anchor = pollAnchor(loaded.current);
    try {
      const body = await getHelperDetail(companyId, target.id, { run: target.run, ...anchor });
      if (mine === seq.current) apply(body, anchor.after);
    } catch (err) {
      if (mine === seq.current) setError(err instanceof HelperRequestError && err.httpStatus === 404 ? "gone" : "load");
    }
  }, [companyId, target.id, target.run, apply]);

  const helper = detail?.helper ?? summary;
  const live = !detail || (helper ? isActive(helper.state) : false);
  usePollWhileVisible(refresh, POLL_MS, live && error !== "gone");

  const loadMore = async () => {
    if (!detail || detail.nextAfter === null || loadingMore) return;
    const offset = detail.nextAfter;
    setLoadingMore(true);
    const mine = ++seq.current;
    try {
      const body = await getHelperDetail(companyId, target.id, { run: target.run, after: offset, since: loaded.current.at(-1)?.id });
      if (mine === seq.current) apply(body, offset);
    } catch {
      if (mine === seq.current) setError("load");
    } finally {
      setLoadingMore(false);
    }
  };

  const brief = detail?.brief ? briefPreview(detail.brief) : null;
  // What Julian reads first: the images it is looking at (newest first) and its own words.
  const shots = steps.flatMap((step) => {
    const path = safeHelperImage(step.image?.url, companyId, target);
    return path ? [{ step, path }] : [];
  }).slice(-SHOTS_SHOWN).reverse();
  const said = steps.filter((step) => step.kind === "text" && step.label);
  return (
    <div className="helpersSheet__view">
      <header className="helpersSheet__head is-detail">
        <button type="button" className="helpersSheet__icon" aria-label="Back to helpers" onClick={(event) => onBack(event.detail === 0)}>
          <ChevronLeft width={22} height={22} aria-hidden="true" />
        </button>
        {helper && <span className="helperDetail__bot"><HelperBot bot={helper.bot} id={helper.id} state={helper.state} size={48} /></span>}
        <div className="helpersSheet__titles">
          <h2 id="helperDetail-title" ref={title} tabIndex={-1}>{helper?.name ?? "Helper"}</h2>
          <p className="helperDetail__meta">
            {helper && <StateChip state={helper.state} />}
            {helper && <span>{startedLine(helper, now)}</span>}
          </p>
        </div>
      </header>
      <div className="helpersSheet__body">
        {error === "gone" && <p className="helpersSheet__empty">This helper is no longer in this chat.</p>}
        {error === "load" && (
          <div className="helpersSheet__banner" role="alert">
            <span>Can’t load this helper — try again.</span>
            <button type="button" onClick={() => void refresh()}>Retry</button>
          </div>
        )}
        {!detail && !error && <SkeletonRows />}
        {detail && (
          <>
            {helper?.lastAction?.text && isActive(helper.state) && (
              <p className="helperDetail__now"><span>Now:</span> <SwapText text={helper.lastAction.text} shimmer={helper.state === "running"} /></p>
            )}
            {detail.result && (
              <section className="helperDetail__section">
                <h3 className="helpersGroup__label">Result</h3>
                <p className="helperDetail__prose">{detail.result}</p>
              </section>
            )}
            {shots.length > 0 && (
              <section className="helperDetail__section">
                <h3 className="helpersGroup__label">What it’s looking at</h3>
                <div className="helperShots">
                  {shots.map(({ step, path }) => (
                    <ProtectedImage key={path} path={path} alt={step.image?.alt || "Image"} frameClass="helperShot"
                      buttonClass="helperShot__button" placeholder="Loading…" revealClass="helperShot__gen" />
                  ))}
                </div>
              </section>
            )}
            {said.length > 0 && (
              <section className="helperDetail__section">
                <h3 className="helpersGroup__label">What it’s saying</h3>
                <ol className="helperSaid">
                  {(saidOpen ? said : said.slice(-SAID_SHOWN)).map((step, index) => (
                    <li key={index}><time>{stepTime(step)}</time><p>{step.label}</p></li>
                  ))}
                </ol>
                {said.length > SAID_SHOWN && (
                  <button type="button" className="helperDetail__more" aria-expanded={saidOpen} onClick={() => setSaidOpen(!saidOpen)}>
                    {saidOpen ? "Show less" : `Show all ${said.length}`}
                  </button>
                )}
              </section>
            )}
            <section className="helperDetail__section">
              <h3 className="helpersGroup__label">What it was asked</h3>
              {brief ? (
                <>
                  <p className="helperDetail__prose">{briefOpen ? detail.brief : brief.text}</p>
                  {brief.clipped && (
                    <button type="button" className="helperDetail__more" aria-expanded={briefOpen} onClick={() => setBriefOpen(!briefOpen)}>
                      {briefOpen ? "Show less" : "Show all"}
                    </button>
                  )}
                </>
              ) : <p className="helperDetail__muted">Not available.</p>}
            </section>
            <section className="helperDetail__section">
              <button type="button" className="helpersGroup__label is-toggle helperDetail__stepsToggle" aria-expanded={stepsOpen}
                aria-controls="helperDetail-steps" onClick={() => setStepsOpen(!stepsOpen)}>
                <span>Every step{steps.length ? ` · ${steps.length}` : ""}</span>
                <ChevronDown width={14} height={14} aria-hidden="true" className={stepsOpen ? "helpersGroup__chevron" : "helpersGroup__chevron is-collapsed"} />
              </button>
              <div id="helperDetail-steps" hidden={!stepsOpen}>
                {detail.truncated && <p className="helperDetail__muted">Only the most recent steps are shown.</p>}
                {steps.length ? (
                  <ol className="helperSteps">
                    {steps.map((step, index) => <HelperStepRow key={index} step={step} image={safeHelperImage(step.image?.url, companyId, target)} />)}
                  </ol>
                ) : <p className="helperDetail__muted">No steps yet.</p>}
                {detail.nextAfter !== null && (
                  <button type="button" className="helperDetail__more" disabled={loadingMore} onClick={() => void loadMore()}>
                    {loadingMore ? "Loading…" : "Load more steps"}
                  </button>
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function stepTime(step: HelperStep): string {
  const t = step.ts ? Date.parse(step.ts) : NaN;
  return Number.isFinite(t) ? new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";
}

function HelperStepRow({ step, image }: { step: HelperStep; image: string | null }) {
  const time = stepTime(step);
  if (step.kind === "text") {
    return (
      <li className="helperStep is-text">
        <time>{time}</time>
        <p>{step.label}</p>
      </li>
    );
  }
  const status = step.status ?? "ok";
  const tone = step.needsYou ? "needs" : status;
  const words = step.words?.trim() || step.label || step.name || "Step";
  return (
    <li className={`helperStep is-tool is-${tone}`}>
      <time>{time}</time>
      <div className="helperStep__main">
        {status === "running"
          ? <span className="helperStep__status is-spin" aria-hidden="true" />
          : <span className="helperStep__status" aria-hidden="true">
              {step.needsYou ? <AlertCircle width={14} height={14} /> : status === "error" ? <X width={14} height={14} /> : <Check width={14} height={14} />}
            </span>}
        <span className="helperStep__words">{words}</span>
        {step.needsYou && <span className="helperStep__flag">Needs you</span>}
        {status === "error" && <span className="helperStep__flag">Failed</span>}
      </div>
      {image && <ProtectedImage path={image} alt={step.image?.alt || "Image"} frameClass="helperStep__image"
        buttonClass="helperStep__imageButton" placeholder="Loading image…" />}
    </li>
  );
}
